import { observer, useLocalObservable } from 'mobx-react';
import { Tooltip } from 'react-tooltip';
import { useStore } from '../../../store/store';
import useHistory from '../../../hooks/useHistory';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../buttons/IconButton';
import ValueWithYear from '../../common/ValueWithYear';
import { PUNCTUATION } from '../../../utils/constants/notation';
import { getTooltipId } from '../../../utils/utils';
import { openInNewTabOnModifier } from '../../../utils/openInNewTab';

// Breakpoint for responsive tooltip positioning (matches mobile LESS breakpoint)
const TOOLTIP_BREAKPOINT = 1200;

/**
 * IndividualRelationsTable - Table showing company relations
 *
 * Columns:
 * - Company name
 * - Location
 * - Role
 * - Share (%) with year superscript + Date of information tooltip
 * - Ultimate shareholder (Yes/No/---)
 * - Sales (€M) with year superscript
 * - Employees with year superscript
 * - Actions (View Details, Add to Watchlist)
 *
 * @param {Object} individual - Individual data (for context)
 * @param {Array} companies - Array of company relations
 * @param {Object} project - Project context for watchlist
 */
const IndividualRelationsTable = observer(({ individual = {}, companies = [], project }) => {
  const { utilsStore, profilerProjectStore, profilerAccessStore, authStore } = useStore();

  if (!companies || !companies.length) {
    return (
      <div className="individual-relations-section">
        <h2 className="section-title">Company relations</h2>
        <div className="no-relations">No company relations found</div>
      </div>
    );
  }

  return (
    <div className="individual-relations-section">
      <h2 className="section-title">Company relations</h2>

      <div className="relations-table">
        {/* Table Header */}
        <div className="table-header">
          <div className="header-cell company-name">Company name</div>
          <div className="header-cell location">Location</div>
          <div className="header-cell role">Role</div>
          <div className="header-cell share">Share (%)</div>
          <div className="header-cell ultimate">Ultimate shareholder</div>
          <div className="header-cell sales">Sales (€M)</div>
          <div className="header-cell employees">Employees</div>
          <div className="header-cell actions">Actions</div>
        </div>

        {/* Table Body */}
        {companies.map((company, index) => (
          <CompanyRelationRow
            key={company.id || index}
            company={company}
            individual={individual}
            project={project}
            utilsStore={utilsStore}
            profilerProjectStore={profilerProjectStore}
            profilerAccessStore={profilerAccessStore}
            authStore={authStore}
          />
        ))}
      </div>
    </div>
  );
});

/**
 * Single company relation row
 */
const CompanyRelationRow = observer(
  ({ company, individual, utilsStore, profilerProjectStore, profilerAccessStore }) => {
    const { navigate } = useHistory();
    const state = useLocalObservable(() => ({
      companyNameTooltipId: getTooltipId(),
      dateInfoTooltipId: getTooltipId(),
      salesDateTooltipId: getTooltipId(),
      employeesDateTooltipId: getTooltipId()
    }));

    const isInWatchlist =
      profilerProjectStore.watchlist.findIndex((c) => c.id === company.id) !== -1;

    const watchlistButtonTooltipContent = !profilerAccessStore.canViewProfilerWatchlist ? (
      <>
        Requires Profiler Lite or Profiler Full access.
        <br />
        Contact <a href="mailto:support@transact.digital">support@transact.digital</a> for
        assistance.
      </>
    ) : isInWatchlist ? (
      <>Remove from watchlist</>
    ) : (
      <>Add to watchlist</>
    );

    const onWatchlistButtonClick = () => {
      profilerProjectStore.addToWatchlist(company);
    };

    const initialTab = 'general';
    const individualName = individual?.fullName || individual?.full_name;

    // Open company details in a new tab (context-menu action and Cmd/Ctrl-click)
    const handleViewDetailsNewTab = () => {
      utilsStore.openCompanyDetails(company.id, {
        tab: initialTab,
        ref: individualName
      });
    };

    // Navigate to company details; Cmd/Ctrl-click opens a new tab instead,
    // matching the right-click menu.
    const handleViewDetails = openInNewTabOnModifier(handleViewDetailsNewTab, () =>
      navigate(
        utilsStore.getCompanyDetailsPath(company.id, {
          tab: initialTab,
          ref: individualName // Back button shows "Back to [individual name]"
        })
      )
    );

    // Format ultimate shareholder display
    const ultimateDisplay =
      company.isUltimate === true
        ? 'Yes'
        : company.isUltimate === false
          ? 'No'
          : PUNCTUATION.EMPTY_VALUE;

    return (
      <div className="table-row">
        {/* Company name - clickable with tooltip (matches profiler search pattern) */}
        <div className="row-cell company-name">
          <span
            className="company-name-text company-name-link"
            id={state.companyNameTooltipId}
            onClick={handleViewDetails}
          >
            {company.name || PUNCTUATION.EMPTY_VALUE}
          </span>
          <Tooltip
            anchorSelect={`#${state.companyNameTooltipId}`}
            content="View details"
            place="bottom"
            variant="dark"
          />
        </div>

        {/* Location */}
        <div className="row-cell location">{company.location || PUNCTUATION.EMPTY_VALUE}</div>

        {/* Role */}
        <div className="row-cell role">{company.roleDisplay || PUNCTUATION.EMPTY_VALUE}</div>

        {/* Share % with Date of Information tooltip */}
        {/* Tooltip only shown when share value exists and dateOfInformation is available */}
        <div className="row-cell share" id={state.dateInfoTooltipId}>
          <ValueWithYear value={company.sharePercent} year={company.shareYear} />
          {company.sharePercent && company.dateOfInformation && (
            <Tooltip
              anchorSelect={`#${state.dateInfoTooltipId}`}
              content={`Date of information: ${company.dateOfInformation}`}
              place={utilsStore.windowWidth > TOOLTIP_BREAKPOINT ? 'bottom-center' : 'bottom-end'}
              variant="dark"
              className={
                utilsStore.windowWidth <= TOOLTIP_BREAKPOINT ? 'date-info-tooltip-right-arrow' : ''
              }
            />
          )}
        </div>

        {/* Ultimate shareholder */}
        <div className="row-cell ultimate">{ultimateDisplay}</div>

        {/* Sales - tooltip only shown when data is from basic/estimate source */}
        <div className="row-cell sales" id={state.salesDateTooltipId}>
          <ValueWithYear value={company.revenueValue} year={company.revenueYear} />
          {company.isRevenueBasic && (
            <Tooltip
              anchorSelect={`#${state.salesDateTooltipId}`}
              content="Based on self-disclosure or estimate"
              place={utilsStore.windowWidth > TOOLTIP_BREAKPOINT ? 'bottom-center' : 'bottom-end'}
              variant="dark"
              className={
                utilsStore.windowWidth <= TOOLTIP_BREAKPOINT ? 'date-info-tooltip-right-arrow' : ''
              }
            />
          )}
        </div>

        {/* Employees - tooltip only shown when data is from basic/estimate source */}
        <div className="row-cell employees" id={state.employeesDateTooltipId}>
          <ValueWithYear value={company.employeesValue} year={company.employeesYear} />
          {company.isEmployeesBasic && (
            <Tooltip
              anchorSelect={`#${state.employeesDateTooltipId}`}
              content="Based on self-disclosure or estimate"
              place={utilsStore.windowWidth > TOOLTIP_BREAKPOINT ? 'bottom-center' : 'bottom-end'}
              variant="dark"
              className={
                utilsStore.windowWidth <= TOOLTIP_BREAKPOINT ? 'date-info-tooltip-right-arrow' : ''
              }
            />
          )}
        </div>

        {/* Actions */}
        {/* Reference: CompanyResult.jsx desktop-only/mobile-only pattern */}
        <div className="row-cell actions">
          {/* Desktop - Icon only with tooltip */}
          <IconButton
            wrapClassName="view-details-btn desktop-only"
            icon={ICON_BUTTON_ICONS.VIEW_DETAILS_CUSTOM1}
            type={ICON_BUTTON_TYPES.BLUE}
            onClick={handleViewDetails}
            tooltipText="View details"
            linkContextMenu={[
              {
                icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
                text: 'Open in new tab',
                onClick: handleViewDetailsNewTab
              }
            ]}
          />
          {/* Mobile - Filled text button */}
          <IconButton
            wrapClassName="view-details-btn mobile-only"
            filled
            type={ICON_BUTTON_TYPES.BLUE}
            onClick={handleViewDetails}
            innerText="View details"
            linkContextMenu={[
              {
                icon: ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT,
                text: 'Open in new tab',
                onClick: handleViewDetailsNewTab
              }
            ]}
          />
          <IconButton
            wrapClassName="watchlist-action-btn"
            type={ICON_BUTTON_TYPES.BLUE}
            icon={ICON_BUTTON_ICONS.WATCHLIST}
            disabled={
              !!profilerProjectStore.companiesInWatchlistAction[company.id] ||
              !profilerAccessStore.canViewProfilerWatchlist
            }
            tooltipContent={watchlistButtonTooltipContent}
            tooltipPosition="bottom-end"
            tooltipClickable={!profilerAccessStore.canViewProfilerWatchlist}
            filled={isInWatchlist}
            onClick={onWatchlistButtonClick}
          />
        </div>
      </div>
    );
  }
);

export default IndividualRelationsTable;
